import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import {
  resetModalSellVehiclesFilters,
  changeModalSellVehiclesFilterRules,
  changeModalSellVehiclesFilterConditions,
  selectSellVehiclesState,
} from '@store';
import { sellVehiclesServices, vehiclesServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { getSellVehiclesRequestPayload } from '@helpers';
import { LIMIT } from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';
import { handleSellVehicles, handleVehiclesEnums } from './utils';

export const SELL_VEHICLES_PAGE_SIZE = LIMIT;

export const useSellVehiclesOperations = () => {
  const dispatch = useDispatch();

  const { filters } = useSelector(selectSellVehiclesState);

  const { query, isReady } = useRouter();

  const {
    page = 1,
    pageSize = SELL_VEHICLES_PAGE_SIZE,
    orderBy = '',
    order = '',
    query: searchQuery = '',
    ...others
  } = query;

  const {
    data: sellVehiclesData,
    isLoading: isLoadingSellVehicles,
    isError: isErrorSellVehicles,
    error: sellVehiclesError,
    refetch: refetchSellVehicles,
  } = useQuery(
    [
      QUERY_KEYS.GET_SELL_VEHICLES_KEY,
      page,
      pageSize,
      orderBy,
      order,
      others,
      searchQuery,
      filters.modalFiltersRules,
    ],
    () => {
      const payload = getSellVehiclesRequestPayload(
        getCleanedUrlParams(query),
        filters.modalFiltersRules
      );

      return sellVehiclesServices.getSellVehicles({
        params: {
          orderBy,
          order,
          page,
          query: searchQuery,
          pageSize: parseInt(pageSize, 10) || SELL_VEHICLES_PAGE_SIZE,
        },
        payload,
      });
    },
    { enabled: isReady }
  );

  const vehiclesEnumsResponse = useQuery(
    QUERY_KEYS.GET_SERVICE_VEHICLES_BRANDS_KEY,
    () => vehiclesServices.getVehicleEnums()
  );

  const enumsResponse = useQuery([QUERY_KEYS.GET_SELL_VEHICLES_ENUMS_KEY], () =>
    sellVehiclesServices.getSellVehiclesEnums()
  );

  const sellVehicles = useMemo(
    () => handleSellVehicles(sellVehiclesData?.data || []),
    [sellVehiclesData]
  );

  const total = useMemo(
    () => sellVehiclesData?.meta?.total,
    [sellVehiclesData]
  );

  const handleResetModalFilters = useCallback(() => {
    dispatch(resetModalSellVehiclesFilters());
  }, [dispatch]);

  const handleOnChangeModalFiltersRules = useCallback(
    (rules) => {
      dispatch(changeModalSellVehiclesFilterRules(rules));
    },
    [dispatch]
  );

  const handleOnChangeModalFiltersConditions = useCallback(
    (conditions) => {
      dispatch(changeModalSellVehiclesFilterConditions(conditions));
    },
    [dispatch]
  );

  useEffect(() => {
    if (sellVehiclesError) {
      showError(sellVehiclesError);
    }
  }, [sellVehiclesError]);

  useEffect(() => {
    if (vehiclesEnumsResponse?.error) {
      showError(vehiclesEnumsResponse.error);
    }
  }, [vehiclesEnumsResponse?.error]);

  useEffect(() => {
    return () => {
      handleResetModalFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vehiclesEnums = useMemo(
    () => handleVehiclesEnums(vehiclesEnumsResponse?.data?.data || {}),
    [vehiclesEnumsResponse?.data?.data]
  );

  return {
    page,
    total,
    loading: isLoadingSellVehicles,
    sellVehicles,
    pageSize: parseInt(pageSize, 10) || SELL_VEHICLES_PAGE_SIZE,
    hasErrors: isErrorSellVehicles,
    filters,
    enums: enumsResponse?.data?.data || {},
    vehiclesEnums,
    getSellVehicles: refetchSellVehicles,
    handleOnChangeModalFiltersRules,
    handleOnChangeModalFiltersConditions,
  };
};
